import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { ExtendedProjectInfoDc, ProjectConfigurationDc, ResourceTypeLink } from "@evergis/api";
import { RoutePanelName } from "../../appConfig/types";
import { DEFAULT_BASE_MAP, DEFAULT_PROJECT_CONTENT } from "../../evergis/core/project/createProjectContent";
import { i18n } from "../../i18n";
import { BaseMapItem } from "../../modules/MapControls/components/BasemapSelect/BasemapBlock";
import { HTTPStatus } from "../../utils/constants";
import { getTempResourceName } from "../../utils/resourceName";
import { getAppConfigModules } from "../appConfig";
import { goToMainBase, redirectToCatalog } from "../location/location";
import { setTempSourceLayers } from "../tempSourceLayers/tempSourceLayers";
import { getUser, setUser } from "../user";
import { waitAction } from "../waitAction";
import {
  createTemporaryProject,
  reset,
  setError,
  setLoading,
  setProject,
  setProjectName,
  updateProject,
} from "./project";
import { defaultState, reset as resetMapView, setMapView } from "../mapView/mapView";
import { api } from "../../evergis/api";
import { getProject } from "./selectors";
import { isEditable } from "../../evergis/core/permissions";
import { notifyError } from "../notifications/notifications";
import { ProjectInfo } from "../../interfaces/resourceInfo";
import { project as projectResource } from "../../resources/projects";

const apiFetchProject = (name: string) => api.projects.findOne(name);
const apiUpdateProject = (resource: ProjectInfo) => api.projects.update(resource as ExtendedProjectInfoDc);
const apiCreateProject = (resource: ExtendedProjectInfoDc) => api.projects.create(resource);

// TODO refactoring
function* resetProjectBeforeFetch() {
  yield put(reset());
  yield put(setMapView(defaultState));
  yield put(resetMapView());
  yield put(setLoading(true));
  yield put(setTempSourceLayers([]));
}

function* fetchProject({ payload: projectName }: ReturnType<typeof setProjectName>) {
  const currentProject: ProjectInfo = yield select(getProject);
  const currentProjectName = currentProject?.name;

  if (currentProjectName === projectName) {
    return;
  }

  if (currentProject) {
    yield* resetProjectBeforeFetch();
  }

  try {
    yield put(setLoading(true));
    // TODO create something for easy requests
    const project: ExtendedProjectInfoDc = yield call(apiFetchProject, projectName);

    yield put(setProject(project));
  } catch (error) {
    yield put(setError(error));

    if (error?.response?.status === HTTPStatus.NotFound) {
      yield put(notifyError({ title: i18n.t("projectNotFound", { ns: "projectPanel" }) }));

      yield put(createTemporaryProject());
    } else {
      yield put(notifyError({ title: i18n.t("projectSavingError", { ns: "projectPanel" }) }));
    }
  }
}

function* mutateProject() {
  try {
    yield put(setLoading(true));

    const currentProject: ProjectInfo = yield select(getProject);
    const ableToUpdate = isEditable(currentProject?.permissions);

    if (ableToUpdate) {
      const project: ExtendedProjectInfoDc = yield call(apiUpdateProject, {
        ...currentProject,
        layersCount: currentProject.layersCount || 0,
      });

      yield put(setProject(project));

      projectResource.write({
        input: project.name,
        reducer: value => ({ ...value, ...project }),
      });
    }
  } catch (error) {
    yield put(setError(error));

    yield put(notifyError({ title: i18n.t("projectSavingError", { ns: "projectPanel" }) }));
  }
}

function* createTempProject() {
  try {
    const user = yield select(getUser);

    if (!user) {
      yield call(waitAction, setUser);
    }

    const modules = yield select(getAppConfigModules);
    const { options: baseMapSelectOptions } =
      modules.find(({ name: moduleName }) => moduleName === "basemap-select") || {};
    const { options: mapOptions } = modules.find(({ name: moduleName }) => moduleName === "map") || {};
    const baseMapItems = baseMapSelectOptions ? (baseMapSelectOptions.items as BaseMapItem[]) : [];
    const baseMapName = baseMapItems[0]?.name || DEFAULT_BASE_MAP;
    const position = mapOptions?.defaultPosition || DEFAULT_PROJECT_CONTENT.position;
    const resolution = mapOptions?.defaultResolution || DEFAULT_PROJECT_CONTENT.resolution;
    const srid = mapOptions?.srid || DEFAULT_PROJECT_CONTENT.srid;
    const defaultProjectContent: ProjectConfigurationDc = {
      baseMapName,
      position,
      resolution,
      srid,
    };
    const tempProject: ExtendedProjectInfoDc = {
      alias: i18n.t("newMap", { ns: "catalog" }),
      description: "",
      preview: null,
      content: defaultProjectContent,
      name: getTempResourceName(),
    };

    const { name } = yield call(apiCreateProject, tempProject);

    yield put(goToMainBase({ project: name, panel: RoutePanelName.Layers }));
  } catch (error) {
    yield put(setError(error));

    yield put(notifyError({ title: i18n.t("projectCreatingError", { ns: "projectPanel" }) }));
    yield put(redirectToCatalog({ tab: ResourceTypeLink.Project }));
  }
}

function* projectSaga() {
  yield takeLatest(setProjectName, fetchProject);
  yield takeEvery(updateProject, mutateProject);
  yield takeEvery(createTemporaryProject, createTempProject);
}

export default projectSaga;
